import { config } from '../config';

// API Base URL from centralized config with fallbacks
const API_BASE_URL = config.apiBaseUrl;

// ===== SIMPLE ENCRYPTION UTILITY =====
class SimpleEncryption {
  private static readonly key = 'StrymHub_2024_Secure_Key_v1';
  
  static encrypt(text: string): string {
    try {
      // Simple XOR encryption with base64 encoding
      const encrypted = text.split('').map((char, i) => 
        String.fromCharCode(char.charCodeAt(0) ^ this.key.charCodeAt(i % this.key.length))
      ).join('');
      return btoa(encrypted);
    } catch {
      return text; // Fallback to original if encryption fails
    }
  }
  
  static decrypt(encrypted: string): string {
    try {
      const decoded = atob(encrypted);
      return decoded.split('').map((char, i) => 
        String.fromCharCode(char.charCodeAt(0) ^ this.key.charCodeAt(i % this.key.length))
      ).join('');
    } catch {
      return ''; // Return empty string if decryption fails
    }
  }
}

// ===== SECURE TOKEN MANAGEMENT =====
class SecureTokenManager {
  private static instance: SecureTokenManager;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private readonly STORAGE_KEY = '_app_secure_session';
  
  private constructor() {
    this.loadFromSecureStorage();
    this.migrateOldTokens(); // Migrate from old localStorage
  }
  
  public static getInstance(): SecureTokenManager {
    if (!SecureTokenManager.instance) {
      SecureTokenManager.instance = new SecureTokenManager();
    }
    return SecureTokenManager.instance;
  }
  
  private migrateOldTokens(): void {
    // Migrate existing localStorage tokens to encrypted storage
    const oldAccess = localStorage.getItem('access_token');
    const oldRefresh = localStorage.getItem('refresh_token');
    
    if (oldAccess && oldRefresh && !this.accessToken) {
      this.setTokens(oldAccess, oldRefresh);
      // Clear old tokens
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  }
  
  setTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    
    // Encrypt tokens before storing
    const tokenData = {
      access: accessToken,
      refresh: refreshToken,
      timestamp: Date.now()
    };
    
    const encrypted = SimpleEncryption.encrypt(JSON.stringify(tokenData));
    sessionStorage.setItem(this.STORAGE_KEY, encrypted);
  }
  
  getAccessToken(): string | null {
    return this.accessToken;
  }
  
  getRefreshToken(): string | null {
    return this.refreshToken;
  }
  
  private loadFromSecureStorage(): void {
    try {
      const encrypted = sessionStorage.getItem(this.STORAGE_KEY);
      if (encrypted) {
        const decrypted = SimpleEncryption.decrypt(encrypted);
        if (decrypted) {
          const tokenData = JSON.parse(decrypted);
          
          // Check if tokens are not too old (24 hours max)
          const age = Date.now() - tokenData.timestamp;
          if (age < 24 * 60 * 60 * 1000) {
            this.accessToken = tokenData.access;
            this.refreshToken = tokenData.refresh;
          } else {
            this.clearTokens();
          }
        }
      }
    } catch {
      this.clearTokens();
    }
  }
  
  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    sessionStorage.removeItem(this.STORAGE_KEY);
    
    // Also clear old localStorage tokens
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }
  
  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }
}

// Initialize secure token manager
const secureTokenManager = SecureTokenManager.getInstance();

// ===== BACKWARD COMPATIBLE TOKEN MANAGEMENT =====
export const tokenManager = {
  getAccessToken: (): string | null => {
    return secureTokenManager.getAccessToken();
  },

  getRefreshToken: (): string | null => {
    return secureTokenManager.getRefreshToken();
  },

  setTokens: (accessToken: string, refreshToken: string): void => {
    secureTokenManager.setTokens(accessToken, refreshToken);
  },

  clearTokens: (): void => {
    secureTokenManager.clearTokens();
  },

  isTokenExpired: (token: string): boolean => {
    return secureTokenManager.isTokenExpired(token);
  }
};

// ===== SECURE USER MANAGEMENT =====
class SecureUserManager {
  private static instance: SecureUserManager;
  private userData: unknown = null;
  private readonly USER_STORAGE_KEY = '_app_user_session';
  
  private constructor() {
    this.loadFromSecureStorage();
    this.migrateOldUserData(); // Migrate from old localStorage
  }
  
  public static getInstance(): SecureUserManager {
    if (!SecureUserManager.instance) {
      SecureUserManager.instance = new SecureUserManager();
    }
    return SecureUserManager.instance;
  }
  
  private migrateOldUserData(): void {
    // Migrate existing localStorage user data to encrypted storage
    const oldAccount = localStorage.getItem('account');
    
    if (oldAccount && !this.userData) {
      this.setUser(JSON.parse(oldAccount));
      // Clear old user data
      localStorage.removeItem('account');
    }
  }
  
  setUser(user: unknown): void {
    this.userData = user;
    
    // Encrypt user data before storing
    const userDataWithTimestamp = {
      user: user,
      timestamp: Date.now()
    };
    
    const encrypted = SimpleEncryption.encrypt(JSON.stringify(userDataWithTimestamp));
    sessionStorage.setItem(this.USER_STORAGE_KEY, encrypted);
  }
  
  getUser(): unknown {
    return this.userData;
  }
  
  private loadFromSecureStorage(): void {
    try {
      const encrypted = sessionStorage.getItem(this.USER_STORAGE_KEY);
      if (encrypted) {
        const decrypted = SimpleEncryption.decrypt(encrypted);
        if (decrypted) {
          const userData = JSON.parse(decrypted);
          
          // Check if user data is not too old (24 hours max)
          const age = Date.now() - userData.timestamp;
          if (age < 24 * 60 * 60 * 1000) {
            this.userData = userData.user;
          } else {
            this.clearUser();
          }
        }
      }
    } catch {
      this.clearUser();
    }
  }
  
  clearUser(): void {
    this.userData = null;
    sessionStorage.removeItem(this.USER_STORAGE_KEY);
    
    // Also clear old localStorage user data
    localStorage.removeItem('account');
  }
}

// Initialize secure user manager
const secureUserManager = SecureUserManager.getInstance();

// ===== BACKWARD COMPATIBLE USER MANAGEMENT =====
export const userManager = {
  getUser: () => {
    return secureUserManager.getUser();
  },

  setUser: (user: unknown) => {
    secureUserManager.setUser(user);
  },

  clearUser: () => {
    secureUserManager.clearUser();
  }
};

// ===== HTTP CLIENT =====
const createTimeoutController = (timeoutMs = 30000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return { controller, timeoutId };
};

// CSRF Token Helper - Removed as Java backend uses JWT, not CSRF tokens

// ===== SHARED FETCH MECHANICS (token attach + 401 refresh-retry) =====
// This is the ONE place that knows how to attach the current access token
// and how to refresh-then-retry on a 401. Every service in the app should
// route its authenticated fetches through this (or makeAuthenticatedRequest
// below, which layers JSON parsing + generic error throwing on top of it),
// rather than re-implementing token attachment locally. Callers that need
// their own status-code-specific error handling can use this directly and
// inspect the returned Response themselves.
/**
 * A refresh already in flight, shared by every caller that needs one.
 *
 * Without this, a page firing eight parallel requests after expiry sent eight
 * concurrent POST /auth/refresh calls, each with the same refresh token, each
 * writing a different token pair over the last. It worked only because the backend
 * does not invalidate the old refresh token on use - a race that survived on luck.
 * At a 15-minute token that stops being rare and becomes every page load.
 */
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Exchanges the refresh token for a new access token. Returns null if it could not.
 *
 * Concurrent callers share one request. The promise is cleared when it settles, so
 * a later expiry starts a fresh one.
 */
const refreshAccessToken = async (): Promise<string | null> => {
  // Checked before anything is stored in refreshInFlight.
  //
  // Doing it inside the promise below was a real bug: with no refresh token the
  // async function returns without ever awaiting, so it runs to completion
  // synchronously - clearing refreshInFlight in its own `finally` - and only then
  // does the assignment below overwrite that null with a settled promise. From then
  // on every refresh returned that stale promise without ever calling the endpoint.
  // Single-flight was poisoned for the life of the page by one attempt made while
  // logged out. A test caught it; it is not visible by reading.
  const refreshToken = tokenManager.getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  if (refreshInFlight) {
    return refreshInFlight;
  }

  const pending = (async (): Promise<string | null> => {
    try {
      const { controller, timeoutId } = createTimeoutController(15000);
      let response: Response;
      try {
        response = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${refreshToken}`,
          },
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }

      if (response.ok) {
        const data = await response.json();
        tokenManager.setTokens(data.accessToken, data.refreshToken);
        return data.accessToken as string;
      }

      // 401/403 means the refresh token itself is dead - revoked, expired, or issued
      // before a password change. The session is over, so end it here rather than
      // leaving the user looking signed in while every request fails.
      if (response.status === 401 || response.status === 403) {
        tokenManager.clearTokens();
        userManager.clearUser();
      }

      return null;
    } catch {
      // A network failure says nothing about whether the session is still valid, so
      // the tokens are deliberately left alone. Logging someone out because their
      // train went into a tunnel would be worse than the failed request.
      return null;
    }
  })();

  refreshInFlight = pending;
  // Cleared only if this is still the current attempt, so a slow settle cannot
  // discard a newer one that has already started.
  void pending.finally(() => {
    if (refreshInFlight === pending) {
      refreshInFlight = null;
    }
  });

  return pending;
};

/**
 * A usable access token, refreshed first if the current one has expired.
 *
 * For callers that genuinely cannot go through {@link authenticatedFetch} - the
 * STOMP clients, which authenticate over a WebSocket handshake rather than HTTP.
 * It shares the same single-flight refresh, so a WebSocket reconnecting at the same
 * moment a page loads does not start a second one.
 *
 * Returns null when there is nothing to authenticate with; the caller decides
 * whether to connect anonymously or not at all.
 */
const ensureFreshToken = async (): Promise<string | null> => {
  const token = tokenManager.getAccessToken();
  if (token && !tokenManager.isTokenExpired(token)) {
    return token;
  }
  if (!tokenManager.getRefreshToken()) {
    return null;
  }
  return refreshAccessToken();
};

const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  let token = tokenManager.getAccessToken();

  // Refresh BEFORE sending, when the token is already known to be expired.
  //
  // This used to send the request deliberately unauthenticated, wait for the 401,
  // and only then refresh - a wasted round trip on every request after expiry.
  // Negligible when that happened once a day; constant at a 15-minute token.
  if ((!token || tokenManager.isTokenExpired(token)) && tokenManager.getRefreshToken()) {
    token = await refreshAccessToken();
  }

  // A caller-supplied AbortSignal wins, and suppresses the default timeout.
  //
  // Overwriting it would silently break cancellation - useChunkedUpload passes an
  // AbortController so a user can cancel an upload mid-flight - and would impose a
  // 30-second deadline on requests that legitimately take longer, which is most of
  // a video chunk upload. A caller that brought its own signal has already decided
  // how long it is willing to wait.
  const callerSignal = options.signal;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  if (!callerSignal) {
    const timeout = createTimeoutController(30000); // 30 seconds timeout
    options.signal = timeout.controller.signal;
    timeoutId = timeout.timeoutId;
  }

  const clearPendingTimeout = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  };

  if (token && !tokenManager.isTokenExpired(token)) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };
  }

  try {
    const response = await fetch(url, options);
    clearPendingTimeout();

    // Still 401 despite the above - the token was rejected for a reason we could not
    // see locally (revoked, or expiring between the check and the call). One refresh,
    // one retry. Never a loop: a second 401 is returned to the caller.
    if (response.status === 401) {
      const fresh = await refreshAccessToken();

      if (fresh) {
        // A fresh timeout for the retry, on the same terms as the first attempt.
        // It previously reused the original options, including an AbortSignal whose
        // timer had already been cleared - so the retried request could hang
        // indefinitely while the caller believed a 30s timeout applied to it.
        const retry = callerSignal ? null : createTimeoutController(30000);
        try {
          return await fetch(url, {
            ...options,
            signal: callerSignal ?? retry!.controller.signal,
            headers: {
              ...options.headers,
              'Authorization': `Bearer ${fresh}`,
            },
          });
        } finally {
          if (retry) {
            clearTimeout(retry.timeoutId);
          }
        }
      }
    }

    return response;
  } catch (error) {
    clearPendingTimeout();
    if (error instanceof Error && error.name === 'AbortError') {
      // Only ours is a timeout. A caller's own signal aborting means the caller
      // cancelled - reporting that as a connection problem would be a lie.
      if (callerSignal) {
        throw error;
      }
      throw new Error('Request timeout. Please check your connection and try again.');
    }
    throw error;
  }
};

const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}): Promise<unknown> => {
  const response = await authenticatedFetch(url, options);

  if (response.status === 401) {
    // Don't automatically logout on 401 - could be admin role issue
    // Just throw an error and let the component handle it
    throw new Error('Your session has expired or you do not have access to this feature. Please log in again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}`);
  }

  return response.json();
};

// ===== AUTH SERVICE =====
export const authService = {
  // Login - Updated for Java Spring Boot Backend
  login: async (email: string, password: string) => {
    const { controller, timeoutId } = createTimeoutController(15000); // 15 seconds timeout
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json();
        // Parse field-level validation errors from backend
        if (errorData.fieldErrors && errorData.fieldErrors.length > 0) {
          const fieldMessages = errorData.fieldErrors.map((fe: { field: string; message: string }) => fe.message);
          const error = new Error(fieldMessages.join('. '));
          (error as unknown as Record<string, unknown>).fieldErrors = errorData.fieldErrors;
          throw error;
        }
        throw new Error(errorData.message || errorData.error || 'Login failed. Please check your email and password.');
      }

      const data = await response.json();
      
      // Store tokens and user data (Java backend uses accessToken/refreshToken)
      if (data.accessToken && data.refreshToken) {
        tokenManager.setTokens(data.accessToken, data.refreshToken);
      }
      
      if (data.user) {
        userManager.setUser(data.user);
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Login timeout. Please check your connection and try again.');
      }
      throw error;
    }
  },

  // Google Login - Updated for Java Spring Boot Backend
  googleLogin: async (googleData: { email: string; google_id: string }) => {
    const { controller, timeoutId } = createTimeoutController(15000); // 15 seconds timeout
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/google-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(googleData),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Google login failed. No account found for this Google account. Please sign up first.');
      }

      const data = await response.json();
      
      // Store tokens and user data (Java backend uses accessToken/refreshToken)
      if (data.accessToken && data.refreshToken) {
        tokenManager.setTokens(data.accessToken, data.refreshToken);
      }
      
      if (data.user) {
        userManager.setUser(data.user);
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Google login timeout. Please check your connection and try again.');
      }
      throw error;
    }
  },

  // Google Signup - Updated for Java Spring Boot Backend
  googleSignup: async (googleData: {
    email: string;
    first_name: string;
    last_name: string;
    google_id: string;
    avatar_url?: string;
  }) => {
    const { controller, timeoutId } = createTimeoutController(15000); // 15 seconds timeout
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/google-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(googleData),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Google signup failed. This email may already be registered. Please try logging in instead.');
      }

      const data = await response.json();
      
      // Store tokens and user data (Java backend uses accessToken/refreshToken)
      if (data.accessToken && data.refreshToken) {
        tokenManager.setTokens(data.accessToken, data.refreshToken);
      }
      
      if (data.user) {
        userManager.setUser(data.user);
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Google signup timeout. Please check your connection and try again.');
      }
      throw error;
    }
  },

  // Register - Updated for Java Spring Boot Backend
  register: async (userData: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
  }) => {
    // Generate username from email
    const username = userData.email.split('@')[0];
    
    const { controller, timeoutId } = createTimeoutController(30000); // 30 seconds timeout
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.email,
          password: userData.password,
          passwordConfirm: userData.password,
          firstName: userData.first_name,
          lastName: userData.last_name,
          username: username
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json();
        // Parse field-level validation errors from backend
        if (errorData.fieldErrors && errorData.fieldErrors.length > 0) {
          const fieldMessages = errorData.fieldErrors.map((fe: { field: string; message: string }) => fe.message);
          const error = new Error(fieldMessages.join('. '));
          (error as unknown as Record<string, unknown>).fieldErrors = errorData.fieldErrors;
          throw error;
        }
        throw new Error(errorData.message || errorData.error || 'Registration failed. Please check your details and try again.');
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Registration timeout. Please check your connection and try again.');
      }
      throw error;
    }
  },

  // Logout - Updated for Java Spring Boot Backend
  logout: async () => {
    // This used to clear local storage only, on the reasoning that a JWT backend has
    // no logout endpoint. That is true of a stateless JWT and it is exactly the
    // problem: discarding the token here did nothing to the token itself, which kept
    // working for up to 24 hours. Anyone who had copied it was unaffected by the user
    // logging out. POST /auth/logout now revokes it server-side.
    const token = tokenManager.getAccessToken();

    if (token) {
      try {
        const { controller, timeoutId } = createTimeoutController(5000);
        try {
          await fetch(`${API_BASE_URL}/auth/logout`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            signal: controller.signal,
          });
        } finally {
          clearTimeout(timeoutId);
        }
      } catch {
        // Deliberately swallowed. If the request fails the token stays valid until it
        // expires, which is no worse than the old behaviour - but refusing to log the
        // user out locally because the network is down would be strictly worse, and
        // would leave them looking signed in on a device they meant to leave.
      }
    }

    // Always, whatever happened above.
    tokenManager.clearTokens();
    userManager.clearUser();
  },

  // Refresh Token - Updated for Java Spring Boot Backend
  refreshToken: async () => {
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${refreshToken}`
      },
    });

    if (!response.ok) {
      throw new Error('Your session has expired. Please log in again.');
    }

    const data = await response.json();
    tokenManager.setTokens(data.accessToken, data.refreshToken);
    return data;
  },

  // Email Verification - Updated for Java Spring Boot Backend
  verifyEmail: async (email: string, code: string) => {
    
    const { controller, timeoutId } = createTimeoutController(15000); // 15 seconds timeout
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Email verification failed. The code may be incorrect or expired.');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Verification timeout. Please check your connection and try again.');
      }
      throw error;
    }
  },

  // Resend Verification Code - Updated for Java Spring Boot Backend
  resendVerificationCode: async (email: string) => {
    const { controller, timeoutId } = createTimeoutController(15000); // 15 seconds timeout
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Unable to resend verification code. Please wait a moment and try again.');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Resend timeout. Please check your connection and try again.');
      }
      throw error;
    }
  },

  // Request Password Reset - Updated for Java Spring Boot Backend
  requestPasswordReset: async (email: string) => {
    const { controller, timeoutId } = createTimeoutController(15000); // 15 seconds timeout
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Unable to send password reset email. Please verify your email address.');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Password reset request timeout. Please check your connection and try again.');
      }
      throw error;
    }
  },

  // Verify Password Reset Code - Client-side validation only
  // Since backend doesn't have separate code verification endpoint, this is just a placeholder
  verifyPasswordResetCode: async (_email: string, code: string) => {
    // Simple validation - just check if code exists and is 6 digits
    if (!code || code.length !== 6 || !/^\d{6}$/.test(code)) {
      throw new Error('Invalid verification code format');
    }
    
    // Return success - actual verification happens when resetting password
    return { success: true };
  },

  // Confirm Password Reset - Updated for Java Spring Boot Backend
  confirmPasswordReset: async (email: string, code: string, newPassword: string, confirmPassword?: string) => {
    // Validate passwords match on client side
    if (confirmPassword && newPassword !== confirmPassword) {
      throw new Error('Passwords do not match');
    }

    const { controller, timeoutId } = createTimeoutController(15000); // 15 seconds timeout
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          code, 
          newPassword 
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Password reset failed. The code may be incorrect or expired. Please request a new reset.');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Password reset timeout. Please check your connection and try again.');
      }
      throw error;
    }
  }
};

// ===== PROFILE SERVICE =====
export const profileService = {
  getProfile: async () => {
    return makeAuthenticatedRequest(`${API_BASE_URL}/auth/profile`);
  },

  updateProfile: async (profileData: unknown) => {
    let response;
    if (profileData instanceof FormData) {
      // Handle file uploads
      response = await makeAuthenticatedRequest(`${API_BASE_URL}/auth/profile`, {
        method: 'PUT',
        body: profileData,
      });
    } else {
      // Handle JSON data
      response = await makeAuthenticatedRequest(`${API_BASE_URL}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData),
      });
    }
    
    // Update stored user data if user info is returned
    if (response && typeof response === 'object' && 'user' in response) {
      userManager.setUser((response as { user: unknown }).user);
    }
    
    return response;
  }
};

// ===== MAIN UNIFIED AUTH OBJECT =====
const unifiedAuth = {
  // Token management
  getAccessToken: tokenManager.getAccessToken,
  getRefreshToken: tokenManager.getRefreshToken,
  setTokens: tokenManager.setTokens,
  clearTokens: tokenManager.clearTokens,
  
  // Authentication status
  isAuthenticated: (): boolean => {
    const token = tokenManager.getAccessToken();
    return token !== null && !tokenManager.isTokenExpired(token);
  },

  isLoggedIn: (): boolean => {
    return unifiedAuth.isAuthenticated() && userManager.getUser() !== null;
  },

  // Password reset methods (exposed at top level for convenience)
  requestPasswordReset: authService.requestPasswordReset,
  verifyPasswordResetCode: authService.verifyPasswordResetCode,
  confirmPasswordReset: authService.confirmPasswordReset,

  // Low-level authenticated fetch: attaches the current token and does a
  // single refresh-then-retry on 401, but returns the raw Response so the
  // caller can apply its own status-code/error-shape handling. Prefer this
  // over rolling a local fetch+Authorization-header helper in a service file.
  fetch: authenticatedFetch,

  // For the STOMP clients, which authenticate over a WebSocket handshake and so
  // cannot use `fetch` above. Shares its single-flight refresh.
  ensureFreshToken,

  // Services
  auth: authService,
  profile: profileService,
  user: userManager,

  // HTTP client for authenticated requests
  request: makeAuthenticatedRequest,
};

export default unifiedAuth;